import React, { useState } from "react";
import {
  CheckCircle2, AlertTriangle, Sparkles, FileText, ClipboardList, Layers,
  FileCheck2, ArrowLeft, ArrowRight,
} from "lucide-react";

/**
 * Click-through demo shown on the Landing hero.
 * Four tabs cycling through the actual case-editor experience without
 * requiring sign-in. Pure client-side; hard-coded sample matter.
 */
const C = {
  navy: "#101C3A", blue: "#2563EB", blueSoft: "#EFF6FF",
  coral: "#FF6B35", coralSoft: "#FFF7ED", teal: "#14B8A6",
  ink: "#172033", slate: "#475569", slateSoft: "#F1F5F9",
};

const STEPS = [
  { id: "intake", label: "1. Intake", Icon: ClipboardList },
  { id: "evidence", label: "2. Evidence", Icon: Layers },
  { id: "score", label: "3. ClaimReady", Icon: Sparkles },
  { id: "draft", label: "4. Draft", Icon: FileCheck2 },
];

export default function ProductTour({ testid = "product-tour" }) {
  const [idx, setIdx] = useState(0);
  const step = STEPS[idx];

  return (
    <div
      className="relative rounded-2xl overflow-hidden border shadow-2xl"
      style={{
        background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
        borderColor: "rgba(255,255,255,0.08)",
        boxShadow: "0 40px 80px -30px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)",
      }}
      data-testid={testid}
    >
      {/* Traffic-light bar */}
      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b" style={{ borderColor: C.slateSoft }}>
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#FF605C" }} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#FFBD44" }} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#00CA4E" }} />
        <div className="ml-4 text-[10px] font-mono tracking-wider text-slate-500">
          prose.claims / matters / doe-v-acme · <span style={{ color: C.coral }}>Live Tour</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b" style={{ borderColor: C.slateSoft, background: "#FAFBFC" }}>
        {STEPS.map((s, i) => {
          const Icon = s.Icon;
          const active = i === idx;
          return (
            <button
              key={s.id}
              onClick={() => setIdx(i)}
              data-testid={`tour-tab-${s.id}`}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-semibold transition-colors relative"
              style={{
                color: active ? C.navy : C.slate,
                background: active ? "#FFFFFF" : "transparent",
              }}
            >
              <Icon className="w-3.5 h-3.5" style={{ color: active ? C.blue : C.slate }} />
              <span className="hidden sm:inline">{s.label}</span>
              <span className="sm:hidden">{i + 1}</span>
              {active && (
                <span
                  aria-hidden="true"
                  className="absolute left-0 right-0 bottom-0 h-0.5"
                  style={{ background: C.blue }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Panel */}
      <div className="p-5 md:p-6 min-h-[360px]">
        {step.id === "intake" && <IntakePanel />}
        {step.id === "evidence" && <EvidencePanel />}
        {step.id === "score" && <ScorePanel />}
        {step.id === "draft" && <DraftPanel />}
      </div>

      {/* Footer nav */}
      <div className="flex items-center justify-between px-5 py-3 border-t text-[11px]" style={{ borderColor: C.slateSoft, background: "#FAFBFC" }}>
        <button
          onClick={() => setIdx((i) => Math.max(0, i - 1))}
          disabled={idx === 0}
          data-testid="tour-prev"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded font-semibold disabled:opacity-40"
          style={{ color: C.slate }}
        >
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
        <div className="flex items-center gap-1.5">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className="w-1.5 h-1.5 rounded-full transition-colors"
              style={{ background: i === idx ? C.blue : "#E2E8F0" }}
            />
          ))}
        </div>
        <button
          onClick={() => setIdx((i) => Math.min(STEPS.length - 1, i + 1))}
          disabled={idx === STEPS.length - 1}
          data-testid="tour-next"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded font-semibold text-white disabled:opacity-40"
          style={{ background: C.navy }}
        >
          {idx === STEPS.length - 1 ? "Finish" : "Next"} <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-center py-1.5 border-b" style={{ borderColor: "#F1F5F9" }}>
      <span className="text-[11px] font-mono uppercase tracking-widest" style={{ color: C.slate }}>{label}</span>
      <span className="text-[12px] font-medium truncate ml-3" style={{ color: C.ink }}>{value}</span>
    </div>
  );
}

function IntakePanel() {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest mb-3" style={{ color: C.blue }}>
        Step 1 · Guided intake
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: C.slate }}>Parties</div>
          <Row label="Plaintiff" value="Jane Doe" />
          <Row label="Defendant" value="Acme Corp." />
          <Row label="Defendant 2" value="A. Smith (CEO)" />
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: C.slate }}>Matter</div>
          <Row label="Court" value="N.D. Cal." />
          <Row label="Claim type" value="Breach of contract" />
          <Row label="Damages" value="$18,500 sought" />
        </div>
      </div>
      <div className="mt-4 rounded-md p-3 text-[12px]" style={{ background: C.coralSoft, color: "#7C2D12" }}>
        <div className="flex items-center gap-1.5 font-semibold mb-0.5">
          <AlertTriangle className="w-3.5 h-3.5" /> Consent required per matter
        </div>
        Users acknowledge ProSe.Claims is not a law firm on every new matter.
      </div>
    </div>
  );
}

function EvidencePanel() {
  const rows = [
    { ex: "A", label: "Signed contract", type: "PDF · 340 KB", pinned: true },
    { ex: "B", label: "Payment receipts (May–Aug)", type: "PDF · 1.2 MB", pinned: true },
    { ex: "C", label: "Email thread w/ Acme", type: "PDF · 88 KB", pinned: false },
    { ex: "D", label: "Text messages screenshots", type: "PNG · 3 files", pinned: false },
  ];
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest mb-3" style={{ color: C.blue }}>
        Step 2 · Evidence organized on timeline
      </div>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div
            key={r.ex}
            className="flex items-center gap-3 rounded-md px-3 py-2 border"
            style={{ background: "#FFFFFF", borderColor: "#E2E8F0" }}
          >
            <div
              className="w-8 h-8 rounded flex items-center justify-center font-mono text-xs font-bold shrink-0"
              style={{ background: C.blueSoft, color: C.blue }}
            >
              {r.ex}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-medium truncate" style={{ color: C.ink }}>Exhibit {r.ex}: {r.label}</div>
              <div className="text-[10px]" style={{ color: C.slate }}>{r.type}</div>
            </div>
            {r.pinned && (
              <span className="text-[9px] font-mono px-1.5 py-0.5 rounded" style={{ background: C.blueSoft, color: C.blue }}>
                LINKED TO TIMELINE
              </span>
            )}
          </div>
        ))}
      </div>
      <div className="mt-3 text-[11px]" style={{ color: C.slate }}>
        Free workspace · 5.9 MB of 100 MB used
      </div>
    </div>
  );
}

function ScorePanel() {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest mb-3" style={{ color: C.blue }}>
        Step 3 · ClaimReady Score
      </div>
      <div className="grid grid-cols-3 gap-4 items-center">
        <div className="flex flex-col items-center">
          <div className="relative w-[120px] h-[120px]">
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
              <circle cx="50" cy="50" r="42" strokeWidth="8" fill="none" stroke="#E2E8F0" />
              <circle
                cx="50" cy="50" r="42" strokeWidth="8" fill="none"
                stroke={C.teal}
                strokeDasharray={2 * Math.PI * 42}
                strokeDashoffset={2 * Math.PI * 42 * (1 - 0.82)}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="font-serif-display text-4xl font-semibold" style={{ color: C.navy }}>82</div>
            </div>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest mt-2" style={{ color: C.teal }}>
            Ready for review
          </div>
        </div>
        <div className="col-span-2 space-y-1.5">
          <ScoreLine label="Parties" pct={100} />
          <ScoreLine label="Facts / Timeline" pct={90} />
          <ScoreLine label="Evidence organized" pct={95} />
          <ScoreLine label="Damages calculation" pct={60} warn />
          <ScoreLine label="Jurisdiction" pct={100} />
          <ScoreLine label="Citations pinned" pct={70} />
        </div>
      </div>
      <div className="mt-4 rounded-md p-3 text-[12px]" style={{ background: C.coralSoft, color: "#7C2D12" }}>
        <div className="flex items-center gap-1.5 font-semibold mb-0.5">
          <AlertTriangle className="w-3.5 h-3.5" /> Add damages calculation
        </div>
        Recommended before filing. Tap to open the damages section.
      </div>
    </div>
  );
}

function ScoreLine({ label, pct, warn }) {
  return (
    <div className="flex items-center gap-3">
      <div className="text-[11px] flex-1 truncate" style={{ color: C.ink }}>{label}</div>
      <div className="w-32 h-1.5 rounded relative overflow-hidden" style={{ background: "#E2E8F0" }}>
        <div
          className="absolute inset-y-0 left-0"
          style={{ width: `${pct}%`, background: warn ? C.coral : C.teal }}
        />
      </div>
      <div className="text-[10px] font-mono tabular-nums w-8 text-right" style={{ color: C.slate }}>{pct}%</div>
    </div>
  );
}

function DraftPanel() {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: C.blue }}>
          Step 4 · Draft complaint
        </div>
        <span className="text-[9px] font-mono px-1.5 py-0.5 rounded" style={{ background: C.coralSoft, color: C.coral }}>
          DRAFT · REQUIRES REVIEW
        </span>
      </div>
      <div className="rounded-md border p-4 max-h-[280px] overflow-hidden" style={{ borderColor: "#E2E8F0", background: "#FFFFFF" }}>
        <div className="font-serif-display text-center font-semibold text-[12px]" style={{ color: C.navy }}>
          UNITED STATES DISTRICT COURT
        </div>
        <div className="text-center text-[10px]" style={{ color: C.slate }}>Northern District of California</div>
        <div className="border-t my-2" style={{ borderColor: "#E2E8F0" }} />
        <div className="text-[11px] leading-relaxed" style={{ color: C.ink }}>
          <div><span className="font-semibold">JANE DOE,</span> Plaintiff,</div>
          <div className="my-1">v.</div>
          <div><span className="font-semibold">ACME CORP.</span> and <span className="font-semibold">A. SMITH,</span> Defendants.</div>
        </div>
        <div className="border-t my-2" style={{ borderColor: "#E2E8F0" }} />
        <div className="font-serif-display text-center font-semibold text-[12px] tracking-wider" style={{ color: C.navy }}>
          COMPLAINT
        </div>
        <div className="text-[10px] mt-2 leading-relaxed" style={{ color: C.slate }}>
          I. Parties &nbsp;·&nbsp; II. Jurisdiction and Venue &nbsp;·&nbsp; III. Factual Allegations &nbsp;·&nbsp; IV. Causes of Action &nbsp;·&nbsp; V. Prayer for Relief
        </div>
        <div className="mt-3 text-[11px]" style={{ color: C.ink }}>
          <span className="font-semibold">III. Factual Allegations.</span> On or about March 3, 2025, Plaintiff and
          Defendant Acme Corp. entered into a written services agreement (Ex. A). Plaintiff paid
          $18,500 in installments between May and August 2025 (Ex. B)…
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <span className="text-[10px] font-mono px-2 py-1 rounded flex items-center gap-1" style={{ background: C.blueSoft, color: C.blue }}>
          <FileText className="w-3 h-3" /> Export PDF
        </span>
        <span className="text-[10px] font-mono px-2 py-1 rounded flex items-center gap-1" style={{ background: C.blueSoft, color: C.blue }}>
          <FileText className="w-3 h-3" /> Export DOCX
        </span>
        <span className="text-[10px] font-mono px-2 py-1 rounded flex items-center gap-1" style={{ background: "#F0FDF4", color: "#166534" }}>
          <CheckCircle2 className="w-3 h-3" /> Citation safety rails: on
        </span>
      </div>
    </div>
  );
}
